import { Box, Typography, Button, Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LogoutIcon from "@mui/icons-material/Logout";
import getCookie from "../../utils/cookieUtils";

const BANNER_HEIGHT = 40; // px — also referenced by the body class below

/**
 * Reads the shadow-mode claims out of the JWT cookie without a network call.
 * Returns null when the current session is not a shadow session.
 */
function parseShadowClaims(token) {
  try {
    const payload = token.split(".")[1];
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4), "="
    );
    const decoded = JSON.parse(atob(padded));
    if (decoded.IsShadow !== "true") return null;
    return {
      orgId:      decoded.OrgId       ?? "ukjent",
      issuedBy:   decoded.ShadowIssuedBy ?? "ukjent",
      expiration: decoded.exp ? new Date(decoded.exp * 1000) : null,
    };
  } catch {
    return null;
  }
}

/**
 * Persistent amber banner rendered at the very top of the page whenever the current
 * session is a shadow session.  Shown outside <Drawer/> so it is visible on every route.
 *
 * Clicking "Avslutt" wipes the token cookie + localStorage and sends the user back to
 * the login page — same path as a normal logout.
 */
export default function ShadowModeBanner({ orgName }) {
  const token = getCookie("token");
  if (!token) return null;

  const claims = parseShadowClaims(token);

  // Add / remove a body class so the page content shifts down to clear the banner.
  // We do this on every render so it stays correct after a token change.
  if (claims) {
    document.body.style.paddingTop = `${BANNER_HEIGHT}px`;
  } else {
    document.body.style.paddingTop = "";
  }

  if (!claims) return null;

  const handleExit = () => {
    // Clear auth state exactly like a normal logout.
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("roles");
    sessionStorage.setItem("logout_reason", "shadow_exit");
    window.location.href = "/login";
  };

  const expiresLabel = claims.expiration
    ? `Utløper ${claims.expiration.toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" })}`
    : null;

  return (
    <Box
      sx={{
        position:        "fixed",
        top:             0,
        left:            0,
        right:           0,
        zIndex:          9999,
        backgroundColor: "#ef9f27",
        color:           "#1a1a1a",
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "space-between",
        px:              2,
        py:              0.75,
        boxShadow:       "0 2px 6px rgba(0,0,0,0.25)",
        gap:             1,
      }}
    >
      {/* Left: icon + message */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <VisibilityIcon fontSize="small" />
        <Typography variant="body2" fontWeight={600} noWrap>
          Skyggemodus
        </Typography>
        <Typography variant="body2" noWrap sx={{ opacity: 0.85 }}>
          — du ser{" "}
          <strong>{orgName ?? claims.orgId}</strong> sin data.
          All personinformasjon er anonymisert.
        </Typography>
      </Box>

      {/* Right: expiry chip + exit button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {expiresLabel && (
          <Chip
            label={expiresLabel}
            size="small"
            sx={{ backgroundColor: "rgba(0,0,0,0.12)", fontSize: 11 }}
          />
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={handleExit}
          sx={{
            borderColor: "rgba(0,0,0,0.35)",
            color:       "#1a1a1a",
            fontSize:    12,
            py:          0.25,
            "&:hover":   { backgroundColor: "rgba(0,0,0,0.08)" },
          }}
        >
          Avslutt
        </Button>
      </Box>
    </Box>
  );
}
